import { useEffect, useState } from 'react';
import { api } from '../api.js';
import {
  pushSupported,
  getCurrentPushSubscription,
  enableWebPush,
  pushOffOnThisDevice,
} from '../push.js';
import {
  isIos,
  isStandalone,
  launchedFromLegacyIcon,
  permissionNote,
  currentPushContext,
} from '../push-help.js';

// Top-of-Home nudge when Sensei has no way to reach the keeper.
// Two flavours, checked in order:
//   1. Everything off (no alert email, no push anywhere, no text) —
//      alert-tinted, points at Account settings.
//   2. This device can't get push yet — cyan. On an iPhone browser tab
//      that means "add TankSensei to your Home Screen first" (Apple only
//      allows web push for home-screen apps), shown as three plain
//      steps. An iPhone Home Screen icon added from the old address
//      (aquasensei.app) can never get permission, so it gets steps to
//      re-add the icon instead of a Turn on that would always fail.
//      Everywhere else it's a one-tap "Turn on".
// Dismissable per device; comes back after 30 days rather than never —
// alerts matter too much to lose to one stray tap on the X.

const DISMISS_KEY = 'aqs.notif_nudge_dismissed_at';
const REDISMISS_DAYS = 30;

function isDismissed() {
  try {
    const t = Number(localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(t) && Date.now() - t < REDISMISS_DAYS * 86400000;
  } catch {
    return false;
  }
}

// Nothing reaches the keeper: no alert email (an older server sends only
// email_digest, which says nothing about alert email, so it is read as on),
// no push subscription anywhere, no texts.
export function nothingReachesKeeper(status) {
  if (!status) return false;
  const email = status.email_alerts ?? true;
  return !email && !status.email_digest && status.push_devices === 0 && !status.sms;
}

export function NotificationNudgeBanner({ onOpenAccount, order }) {
  const [dismissed, setDismissed] = useState(isDismissed);
  const [status, setStatus] = useState(null); // server: channels on the account
  const [deviceHasPush, setDeviceHasPush] = useState(true); // optimistic until checked
  const [showIosSteps, setShowIosSteps] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (dismissed) return;
    let alive = true;
    api('/api/notifications/status')
      .then((s) => alive && setStatus(s))
      .catch(() => {});
    getCurrentPushSubscription().then((sub) => {
      if (!alive) return;
      setDeviceHasPush(
        !!sub && typeof Notification !== 'undefined' && Notification.permission === 'granted'
      );
    });
    return () => {
      alive = false;
    };
  }, [dismissed]);

  if (dismissed || !status) return null;

  const nothingOn = nothingReachesKeeper(status);
  const iosNeedsHomeScreen = isIos() && !isStandalone();
  const legacyIcon = isIos() && isStandalone() && launchedFromLegacyIcon();
  const canOfferPush = pushSupported() && status.push_enabled;
  // The keeper turned alerts off on this device in Account: asking again
  // here would undo their choice.
  const needsPushHere =
    !deviceHasPush && !pushOffOnThisDevice() && (canOfferPush || iosNeedsHomeScreen || legacyIcon);

  if (!nothingOn && !needsPushHere) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* private mode — banner just reappears next visit */
    }
    setDismissed(true);
  };

  const turnOnPush = async () => {
    setBusy(true);
    setNote('');
    try {
      await enableWebPush();
      setDeviceHasPush(true);
      setNote('Done — this device will get tank alerts.');
      setTimeout(dismiss, 2500);
    } catch (e) {
      setNote(
        permissionNote(e?.reason, currentPushContext()) ||
          e?.message ||
          'Could not turn on notifications.'
      );
    } finally {
      setBusy(false);
    }
  };

  const alertFlavour = nothingOn;
  const heading = alertFlavour
    ? 'Sensei has no way to reach you'
    : iosNeedsHomeScreen || legacyIcon
      ? 'Get tank alerts on this iPhone'
      : 'Get tank alerts on this device';
  const body = alertFlavour
    ? 'Email, push, and text alerts are all off — if a parameter drifts overnight, nothing will tell you.'
    : legacyIcon
      ? 'This Home Screen icon still opens our old address, and iPhone only allows alerts for the address an icon was added from. Add TankSensei to your Home Screen again to get alerts.'
      : iosNeedsHomeScreen
        ? 'Add TankSensei to your Home Screen first — iPhones only allow alerts for apps that live there. It takes about 20 seconds.'
        : 'Turn on notifications and Sensei can nudge you the moment a parameter needs attention.';

  return (
    <section
      className="slide-in span-2"
      role="status"
      style={{
        order: typeof order === 'number' ? order : -1,
        padding: '12px 14px',
        borderRadius: 14,
        background: alertFlavour ? 'var(--alert-bg)' : 'var(--accent-bg)',
        border: `1px solid ${alertFlavour ? 'var(--alert-border)' : 'var(--accent-border)'}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="font-heading"
            style={{
              fontSize: 13,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: alertFlavour ? 'var(--alert)' : 'var(--cyan)',
              marginBottom: 4,
            }}
          >
            {heading}
          </div>
          <div style={{ fontSize: 15, color: 'var(--text-muted)', lineHeight: 1.5 }}>{body}</div>

          {showIosSteps && (
            <ol
              style={{
                margin: '10px 0 0',
                paddingLeft: 18,
                fontSize: 15,
                color: 'var(--text)',
                lineHeight: 1.8,
              }}
            >
              {legacyIcon && (
                <>
                  <li>Press and hold this TankSensei icon on your Home Screen and remove it.</li>
                  <li>
                    Open <strong>tanksensei.com</strong> in Safari.
                  </li>
                </>
              )}
              <li>
                Tap the <strong>Share</strong> button in Safari (the square with the arrow).
              </li>
              <li>
                Scroll down and tap <strong>Add to Home Screen</strong>.
              </li>
              <li>
                Open TankSensei from the new icon, then turn on alerts in{' '}
                <strong>Account settings</strong>.
              </li>
            </ol>
          )}

          {note && (
            <div
              style={{
                marginTop: 8,
                fontSize: 14,
                color: note.startsWith('Done') ? 'var(--cyan)' : 'var(--alert)',
              }}
            >
              {note}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {alertFlavour ? (
              <button
                onClick={onOpenAccount}
                className="font-heading"
                style={{
                  fontSize: 12,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  padding: '7px 14px',
                  minHeight: 44,
                  borderRadius: 999,
                  background: 'var(--alert)',
                  color: 'var(--on-alert, #fff)',
                }}
              >
                Turn on notifications
              </button>
            ) : iosNeedsHomeScreen || legacyIcon ? (
              <button
                onClick={() => setShowIosSteps((v) => !v)}
                className="font-heading"
                style={{
                  fontSize: 12,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  padding: '7px 14px',
                  minHeight: 44,
                  borderRadius: 999,
                  background: 'var(--cyan)',
                  color: 'var(--cyan-text)',
                }}
              >
                {showIosSteps ? 'Got it' : 'Show me how'}
              </button>
            ) : (
              <button
                onClick={turnOnPush}
                disabled={busy}
                className="font-heading"
                style={{
                  fontSize: 12,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  padding: '7px 14px',
                  minHeight: 44,
                  borderRadius: 999,
                  background: 'var(--cyan)',
                  color: 'var(--cyan-text)',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? 'Turning on…' : 'Turn on'}
              </button>
            )}
            <button
              onClick={dismiss}
              className="font-heading"
              style={{
                fontSize: 12,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                padding: '7px 14px',
                minHeight: 44,
                borderRadius: 999,
                color: 'var(--text-dim)',
                border: '1px solid var(--surface-border)',
              }}
            >
              Later
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss notification reminder"
          style={{
            flexShrink: 0,
            color: 'var(--text-dim)',
            padding: 4,
            minWidth: 44,
            minHeight: 44,
            lineHeight: 1,
            fontSize: 18,
          }}
        >
          ×
        </button>
      </div>
    </section>
  );
}
